import AddLinkIcon from "@mui/icons-material/AddLink";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import {
    type SurveyDailyInvitation,
    type SurveyDailyListRow,
    SurveyDailyRowKind,
    SurveyDailySurveyState
} from "crqlar-react-common/dist/models/survey/surveyDailyList.model";
import type {MouseEvent, ReactNode} from "react";
import {useTranslation} from "react-i18next";
import {DateElement} from "../../common/uiElements/DateElement";
import SurveyInvitationStatusChip from "../invitations/SurveyInvitationStatusChip";
import {isCancelledStay} from "./surveyDailyRows";

export const SURVEY_DAILY_STATE_LABEL_KEYS: Record<SurveyDailySurveyState, string> = {
    [SurveyDailySurveyState.NOT_INVITED]: 'Not invited',
    [SurveyDailySurveyState.INVITED]: 'Invited',
    [SurveyDailySurveyState.ANSWERED]: 'Answered'
};

// Tall enough for a status chip, and the same in every survey column, so one invitation's name,
// status, send and score stay on one visual line across the row.
const INVITATION_LINE_HEIGHT = 32;

const quietTextSx = {
    color: 'text.secondary',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
} as const;

function QuietText({text, asPrint}: {text: string, asPrint: boolean}) {
    if (asPrint) return <>{text}</>;

    return <Box component={'span'} sx={{...quietTextSx, display: 'block'}}>{text}</Box>;
}

interface InvitationLinesProps {
    invitations: SurveyDailyInvitation[]
    render: (invitation: SurveyDailyInvitation) => ReactNode
    align?: 'left' | 'right'
    asPrint?: boolean
}

/** A room's invitations one under the other, newest first; printed one per line. */
export function SurveyDailyInvitationLines({invitations, render, align = 'left', asPrint = false}: InvitationLinesProps) {
    if (!invitations.length) return null;

    if (asPrint) {
        return <>{invitations.map(invitation => <div key={invitation.invitationId}>{render(invitation)}</div>)}</>;
    }

    return (
        <Box sx={{py: 0.5, minWidth: 0}}>
            {invitations.map(invitation => (
                <Box
                    key={invitation.invitationId}
                    sx={{
                        height: INVITATION_LINE_HEIGHT,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
                        minWidth: 0
                    }}
                >
                    {render(invitation)}
                </Box>
            ))}
        </Box>
    );
}

interface SurveysCellProps {
    row: SurveyDailyListRow
    readOnly: boolean
    /** Opens the answer once there is one, else the link. */
    onOpen: (invitation: SurveyDailyInvitation) => void
    onCreateLink: () => void
}

/**
 * A room's surveys, each opening what it has, and the way to hand the guest one more. A group invites
 * nobody, and neither does a cancelled stay - it only shows a link it already had.
 */
export function SurveyDailySurveysCell({row, readOnly, onOpen, onCreateLink}: SurveysCellProps) {
    if (row.kind === SurveyDailyRowKind.GROUP) return null;

    const hasGuest = !!row.profileId;
    const cancelled = isCancelledStay(row);

    if (!row.invitations.length) {
        if (cancelled) return null;
        return <SurveyDailyCreateLinkButton hasGuest={hasGuest} disabled={readOnly} onClick={onCreateLink}/>;
    }

    return (
        <Stack direction={'row'} alignItems={'center'} gap={1} sx={{minWidth: 0}}>
            <Box sx={{flex: 1, minWidth: 0}}>
                <SurveyDailyInvitationLines
                    invitations={row.invitations}
                    render={invitation => (
                        <Link
                            component={'button'}
                            underline={'hover'}
                            color={'inherit'}
                            title={invitation.surveyName}
                            onClick={event => {
                                event.stopPropagation();
                                onOpen(invitation);
                            }}
                            sx={{
                                display: 'block',
                                maxWidth: '100%',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                textAlign: 'left'
                            }}
                        >
                            {invitation.surveyName || '—'}
                        </Link>
                    )}
                />
            </Box>
            {!cancelled && (
                <SurveyDailyCreateLinkButton another hasGuest={hasGuest} disabled={readOnly} onClick={onCreateLink}/>
            )}
        </Stack>
    );
}

interface StatusCellProps {
    row: SurveyDailyListRow
    asPrint?: boolean
}

/**
 * Each invitation's own status. A room nobody invited says so quietly, and a group counts its rooms
 * that got a link, so a half-invited group shows while it is collapsed. Cancelled stays are left out
 * of that count, and say nothing of their own: nobody was there to invite.
 */
export function SurveyDailyStatusCell({row, asPrint = false}: StatusCellProps) {
    const {t} = useTranslation();

    if (row.kind === SurveyDailyRowKind.GROUP) {
        const rooms = row.children.filter(room => !isCancelledStay(room));
        if (!rooms.length) return null;

        const invited = rooms.filter(room => room.surveyState !== SurveyDailySurveyState.NOT_INVITED).length;
        return <QuietText text={t('{{invited}} of {{total}} invited', {invited, total: rooms.length})} asPrint={asPrint}/>;
    }

    if (!row.invitations.length) {
        return isCancelledStay(row) ? null : <QuietText text={t('Not invited')} asPrint={asPrint}/>;
    }

    return (
        <SurveyDailyInvitationLines
            invitations={row.invitations}
            asPrint={asPrint}
            render={invitation => <SurveyInvitationStatusChip status={invitation.status} asPrint={asPrint}/>}
        />
    );
}

interface SentProps {
    sentAt?: Date
    /** Who sent it, or that an automation did. */
    sender: string
    asPrint?: boolean
}

export function SurveyDailySentElement({sentAt, sender, asPrint = false}: SentProps) {
    if (asPrint) {
        return <><DateElement date={sentAt}/>{`${sentAt ? ' · ' : ''}${sender}`}</>;
    }

    return (
        <Stack direction={'row'} alignItems={'baseline'} gap={0.75} sx={{minWidth: 0}}>
            {/* The date keeps its line; a long sender name is what gives way. */}
            <Box component={'span'} sx={{flexShrink: 0, whiteSpace: 'nowrap'}}><DateElement date={sentAt}/></Box>
            <Box component={'span'} sx={{...quietTextSx, minWidth: 0}}>{sender}</Box>
        </Stack>
    );
}

interface CreateLinkProps {
    /** Without a staying guest there is nobody to invite, which the tooltip says. */
    hasGuest: boolean
    disabled: boolean
    /** Beside invitations already out: the icon alone, for one more survey. */
    another?: boolean
    onClick: () => void
}

/** One action in two sizes: the labelled button on an empty room, the same icon alone beside its surveys. */
export function SurveyDailyCreateLinkButton({hasGuest, disabled, another = false, onClick}: CreateLinkProps) {
    const {t} = useTranslation();
    const hint = another ? t('Create a link for another survey') : '';
    const blocked = disabled || !hasGuest;
    const handleClick = (event: MouseEvent) => {
        event.stopPropagation();
        onClick();
    };

    return (
        <Tooltip title={hasGuest ? hint : t('No guest profile attached to reservation')}>
            {/* A disabled button lets the click through to the row, so the wrapper stops it there. */}
            <Box component={'span'} data-no-row-click sx={{display: 'inline-flex', flexShrink: 0}}>
                {another ? (
                    <IconButton
                        size={'small'}
                        color={'primary'}
                        aria-label={t('Create a link for another survey')}
                        disabled={blocked}
                        onClick={handleClick}
                    >
                        <AddLinkIcon fontSize={'small'}/>
                    </IconButton>
                ) : (
                    <Button
                        variant={'text'}
                        size={'small'}
                        startIcon={<AddLinkIcon/>}
                        disabled={blocked}
                        onClick={handleClick}
                    >
                        {t('Create link')}
                    </Button>
                )}
            </Box>
        </Tooltip>
    );
}
